import { useState, useEffect, useCallback, useContext } from 'react';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Textarea,
    Modal, ModalDialog, ModalClose, Checkbox,
} from '@mui/joy';
import { motion } from 'framer-motion';

import CheckCircleRoundedIcon  from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon       from '@mui/icons-material/CancelRounded';
import ArrowBackRoundedIcon    from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon         from '@mui/icons-material/SendRounded';
import SwapHorizRoundedIcon    from '@mui/icons-material/SwapHorizRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetAllSubstitutionRequests,
    APIReviewSubstitutionRequest,
    APIGetBroadcastCandidates,
    APIBroadcastSubstitution,
    APICancelSubstitutionRequest,
    parseApiError,
} from '../../api';
import { useNavigate } from 'react-router';
import MemeLoader from '../../components/MemeLoader';

const STATUS_MAP = {
    pending:   { label: 'Ожидает',        color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    approved:  { label: 'Ищем замену',    color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    taken:     { label: 'Замена найдена', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    rejected:  { label: 'Отклонена',      color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
    cancelled: { label: 'Отменена',       color: '#9898b8', bg: 'rgba(152,152,184,0.12)' },
};

const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38 } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

export default function SubstitutionsPage() {
    const { token, showToast, onRefreshTick, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canReview    = hasPerm('schedule.substitutions.review');
    const canBroadcast = hasPerm('schedule.substitutions.broadcast');

    const [requests,  setRequests]  = useState([]);
    const [loading,   setLoading]   = useState(true);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [manualMode,  setManualMode]  = useState(false);

    const [showAll, setShowAll] = useState(false);

    // Ревью
    const [reviewId,      setReviewId]      = useState(null);
    const [reviewAction,  setReviewAction]  = useState('');
    const [reviewComment, setReviewComment] = useState('');
    const [reviewing,     setReviewing]     = useState(false);
    const [broadcastRequired, setBroadcastRequired] = useState(false);

    // Рассылка
    const [broadcastId,         setBroadcastId]         = useState(null);
    const [broadcastFilters,    setBroadcastFilters]    = useState({
        no_availability: false,
        has_availability: false,
        no_assignment: true,
    });
    const [broadcastCandidates, setBroadcastCandidates] = useState([]);
    const [loadingCandidates,   setLoadingCandidates]   = useState(false);
    const [broadcasting,        setBroadcasting]        = useState(false);
    const [cancellingId, setCancellingId] = useState(null);

    // ── Загрузка ──────────────────────────────────────────────────
    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            const data = await APIGetAllSubstitutionRequests(token);
            setLoading(false);
            if (Array.isArray(data)) { setRequests(data); return true; }
            return false;
        } catch (e) { setLoading(false); return false; }
    }, [token]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    useEffect(() => onRefreshTick(loadRequests), [onRefreshTick, loadRequests]);

    // ── Ревью ─────────────────────────────────────────────────────
    const handleReview = async () => {
        if (reviewAction === 'approve') {
            if (!window.confirm('Точно одобряем заявку на замену?')) return;
        }
        setReviewing(true);
        const res = await APIReviewSubstitutionRequest(token, reviewId, {
            action:  reviewAction,
            comment: reviewComment,
        });
        if (!res?.error) {
            showToast(reviewAction === 'approve' ? 'Заявка одобрена ✓' : 'Заявка отклонена');
            setReviewId(null);
            setReviewComment('');
            await loadRequests();

            // Если одобрили — сразу предлагаем рассылку
            if (reviewAction === 'approve') {
                setBroadcastId(res.id);
                setBroadcastRequired(true);
            }
        } else {
            showToast('Ошибка', 'danger');
        }
        setReviewing(false);
    };

    // ── Кандидаты для рассылки ─────────────────────────────────────
    const loadCandidates = useCallback(async () => {
        if (!broadcastId) return;
        setLoadingCandidates(true);
        const filters = {};
        if (!showAll) {
            if (broadcastFilters.no_availability)  filters.no_availability  = '1';
            if (broadcastFilters.has_availability) filters.has_availability = '1';
            if (broadcastFilters.no_assignment)    filters.no_assignment    = '1';
        }
        // Если showAll — отправляем без фильтров, сервер вернёт всех
        const data = await APIGetBroadcastCandidates(token, broadcastId, filters);
        const list = data?.candidates || [];
        setBroadcastCandidates(list);
        setSelectedIds(new Set(list.map(c => c.id)));
        setLoadingCandidates(false);
    }, [token, broadcastId, broadcastFilters, showAll]);

    useEffect(() => {
        if (broadcastId) {
            setSelectedIds(new Set());
            setManualMode(false);
            loadCandidates();
        }
    }, [broadcastId, broadcastFilters, showAll]); // eslint-disable-line

    // ── Рассылка ──────────────────────────────────────────────────
    const handleBroadcast = async () => {
        setBroadcasting(true);
        // Если кого-то сняли с галочек — шлём только выбранным (account_ids)
        const payload = { ...broadcastFilters };
        if (selectedIds.size !== broadcastCandidates.length) {
            payload.account_ids = [...selectedIds];
        }
        const res = await APIBroadcastSubstitution(token, broadcastId, payload);
        if (!res?.error) {
            showToast(`Рассылка отправлена: ${res.delivered} из ${res.sent_to} доставлено ✓`);
            setBroadcastId(null);
            setBroadcastRequired(false);
            await loadRequests();
        } else {
            showToast('Ошибка рассылки', 'danger');
        }
        setBroadcasting(false);
    };

    // ── Отмена замены ──────────────────────────────────────────────
    const handleCancelSubstitution = async (reqId) => {
        if (!window.confirm('Точно отменить заявку на замену? Она пропадёт у всех.')) return;
        setCancellingId(reqId);
        const res = await APICancelSubstitutionRequest(token, reqId);
        if (!res?.error) {
            showToast('Замена отменена ✓');
            await loadRequests();
        } else {
            showToast(parseApiError(res.error), 'danger');
        }
        setCancellingId(null);
    };

    // ── Форматирование дат (с днём недели, без года) ──────────────
    const WEEKDAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    const MONTHS   = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];

    const fmtOne = (iso) => {
        const d = new Date(iso);
        return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
    };

    const fmtDates = (req) => {
        if (req.start_date === req.end_date) return fmtOne(req.start_date);
        return `${fmtOne(req.start_date)} — ${fmtOne(req.end_date)}`;
    };

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />
            <Box sx={{ maxWidth: 750, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                        onClick={() => navigate('/manager')}
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 36, height: 36,
                            borderRadius: 'var(--joy-radius-md)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'var(--joy-palette-background-level1)',
                            cursor: 'pointer', transition: 'all .15s',
                            '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                        }}
                    >
                        <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                    </Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                        Заявки на замену
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : requests.length === 0 ? (
                    <Typography sx={{ color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 6 }}>
                        Нет заявок на замену
                    </Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {requests.map((req) => {
                            const st = STATUS_MAP[req.status] || STATUS_MAP.pending;
                            return (
                                <Box key={req.id} sx={{
                                    p: 2, borderRadius: 'var(--joy-radius-lg)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {req.account_name}
                                            </Typography>
                                            <Box sx={{
                                                display: 'inline-flex', alignItems: 'center', mt: 0.4,
                                                px: 1, py: 0.3, borderRadius: 'var(--joy-radius-sm)',
                                                bgcolor: 'rgba(6,182,212,0.12)',
                                                border: '1px solid rgba(6,182,212,0.3)',
                                            }}>
                                                <Typography sx={{ fontSize: 12.5, color: '#06b6d4', fontWeight: 700 }}>
                                                    {fmtDates(req)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Chip size="sm" sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, fontSize: 11 }}>
                                            {st.label}
                                        </Chip>
                                    </Box>

                                    {req.reason && (
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                            <b>Причина:</b> {req.reason}
                                        </Typography>
                                    )}
                                    {req.substitute_name && (
                                        <Typography sx={{ fontSize: 12, color: '#22c55e', fontWeight: 600, mb: 0.5 }}>
                                            Заменяет: {req.substitute_name}
                                        </Typography>
                                    )}
                                    {req.review_comment && (
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>
                                            <b>Комментарий:</b> {req.review_comment}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                                        Создана: {new Date(req.date_create).toLocaleString('ru-RU')}
                                    </Typography>

                                    {/* Кнопки */}
                                    {req.status === 'pending' && canReview && (
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                            <Button size="sm" color="success"
                                                startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => { setReviewId(req.id); setReviewAction('approve'); }}
                                                sx={{ fontWeight: 700, fontSize: 12 }}>
                                                Одобрить
                                            </Button>
                                            <Button size="sm" color="danger" variant="outlined"
                                                startDecorator={<CancelRoundedIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => { setReviewId(req.id); setReviewAction('reject'); }}
                                                sx={{ fontWeight: 700, fontSize: 12 }}>
                                                Отклонить
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Кнопки для одобренных: рассылка + отмена */}
                                    {req.status === 'approved' && (
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {canBroadcast && (
                                                <Button size="sm" variant="outlined"
                                                    startDecorator={<SendRoundedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => { setBroadcastId(req.id); setBroadcastRequired(false); }}
                                                    sx={{
                                                        borderColor: '#06b6d4', color: '#06b6d4',
                                                        fontWeight: 700, fontSize: 12,
                                                        '&:hover': { bgcolor: 'rgba(6,182,212,0.08)' },
                                                    }}>
                                                    Разослать фотографам
                                                </Button>
                                            )}
                                            {canReview && (
                                                <Button size="sm" variant="outlined" color="danger"
                                                    loading={cancellingId === req.id}
                                                    startDecorator={<CancelRoundedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => handleCancelSubstitution(req.id)}
                                                    sx={{ fontWeight: 700, fontSize: 12 }}>
                                                    Отменить замену
                                                </Button>
                                            )}
                                            {req.broadcast_sent && (
                                                <Chip size="sm" sx={{
                                                    bgcolor: 'rgba(34,197,94,0.12)', color: '#22c55e',
                                                    fontWeight: 600, fontSize: 10,
                                                }}>
                                                    Рассылка отправлена
                                                </Chip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* ── Модалка ревью ─────────────────────────────────────── */}
            <Modal open={!!reviewId} onClose={() => setReviewId(null)}>
                <ModalDialog sx={{
                    maxWidth: "90vw", width: '100%', bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-xl)',
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 1 }}>
                        {reviewAction === 'approve' ? 'Одобрить замену?' : 'Отклонить замену?'}
                    </Typography>
                    <Textarea
                        minRows={2} value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Комментарий (необязательно)..."
                        sx={{ mb: 2, color: 'white', bgcolor: 'var(--joy-palette-background-level1)' }}
                    />
                    <Button onClick={handleReview} loading={reviewing}
                        color={reviewAction === 'approve' ? 'success' : 'danger'}
                        sx={{ fontWeight: 700 }}>
                        {reviewAction === 'approve' ? 'Одобрить' : 'Отклонить'}
                    </Button>
                </ModalDialog>
            </Modal>

            {/* ── Модалка рассылки ─────────────────────────────────── */}
            <Modal
                open={!!broadcastId}
                onClose={() => {
                    if (broadcastRequired) return; // нельзя закрыть без рассылки
                    setBroadcastId(null);
                }}
            >
                <ModalDialog sx={{
                    maxWidth: "90vw", width: '100%', bgcolor: 'var(--joy-palette-background-surface)',
                    border: '1px solid var(--joy-palette-divider)', borderRadius: 'var(--joy-radius-xl)',
                }}>
                    {!broadcastRequired && <ModalClose />}
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                        Рассылка о замене
                        {broadcastRequired && (
                            <Chip size="sm" color="danger" variant="soft" sx={{ ml: 1, fontSize: 10, fontWeight: 700 }}>
                                Обязательно
                            </Chip>
                        )}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                        {broadcastRequired
                            ? 'Выберите фотографов для рассылки. Рассылка обязательна при одобрении замены.'
                            : 'Выберите, кому отправить уведомление о замене'}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)', fontWeight: 600 }}>
                                Фильтр: кто не работает в эти дни
                            </Typography>
                            <Chip
                                size="sm"
                                variant={showAll ? 'solid' : 'outlined'}
                                color={showAll ? 'primary' : 'neutral'}
                                onClick={() => setShowAll(prev => !prev)}
                                sx={{ cursor: 'pointer', fontWeight: 600, fontSize: 11 }}
                            >
                                {showAll ? 'Показаны все' : 'Показать всех'}
                            </Chip>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                            {showAll
                                ? 'Показаны все фотографы — выберите вручную'
                                : 'Показаны фотографы без назначений на дни замены'}
                        </Typography>
                    </Box>

                    {/* Список кандидатов */}
                    <Box sx={{
                        p: 1.5, borderRadius: 'var(--joy-radius-md)',
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        mb: 2, maxHeight: 200, overflow: 'auto',
                    }}>
                        {loadingCandidates ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <MemeLoader />
                            </Box>
                        ) : broadcastCandidates.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 1 }}>
                                Никто не подходит под фильтры
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-text-secondary)' }}>
                                        Получат уведомление: {selectedIds.size} из {broadcastCandidates.length}
                                    </Typography>
                                    <Typography
                                        onClick={() => setSelectedIds(
                                            selectedIds.size === broadcastCandidates.length
                                                ? new Set()
                                                : new Set(broadcastCandidates.map(c => c.id))
                                        )}
                                        sx={{ fontSize: 11, fontWeight: 700, color: '#06b6d4', cursor: 'pointer' }}
                                    >
                                        {selectedIds.size === broadcastCandidates.length ? 'Снять все' : 'Выбрать всех'}
                                    </Typography>
                                </Box>
                                {broadcastCandidates.map(c => (
                                    <Box key={c.id}
                                        onClick={() => setSelectedIds(prev => {
                                            const next = new Set(prev);
                                            next.has(c.id) ? next.delete(c.id) : next.add(c.id);
                                            return next;
                                        })}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            py: 0.4, px: 0.5, borderRadius: 'var(--joy-radius-sm)',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                                        }}
                                    >
                                        <Checkbox size="sm" checked={selectedIds.has(c.id)} readOnly />
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)' }}>
                                            {c.first_name} {c.last_name}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={handleBroadcast}
                            loading={broadcasting}
                            disabled={selectedIds.size === 0}
                            startDecorator={<SendRoundedIcon sx={{ fontSize: 14 }} />}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                                color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
                                '&:disabled': { opacity: 0.5 },
                            }}
                        >
                            Разослать
                        </Button>
                        {!broadcastRequired && (
                            <Button variant="outlined" color="neutral"
                                onClick={() => setBroadcastId(null)}
                                sx={{ fontWeight: 600 }}>
                                Пропустить
                            </Button>
                        )}
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}